import type { BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { evaluateFetchApiArgs, type AppRoute, fetchApi, isErrorResponse } from '@acre-rewards/lib'
import { getMesaApiRequestHeaders } from '@/src/common/utils/getMesaApiRequestHeaders'
import { type SuccessResult, type EndpointArgs } from './endpoints'
import { refreshAccessToken } from '@/src/common/gql/apolloClient'
import { useTokenStore } from '@/src/common/store/useTokenStore'
import { stringifyMessage } from '@/src/common/hooks/useLogError/stringifyMessage'

export type BaseQueryArgs<Route extends AppRoute> = {
  route: Route
  args: EndpointArgs<Route>
}

async function baseFetch<Route extends AppRoute>(
  { route, args }: BaseQueryArgs<Route>,
  overrideHeaders?: Record<string, string>,
) {
  const baseHeaders = getMesaApiRequestHeaders()
  return await fetchApi(
    evaluateFetchApiArgs(
      route,
      {
        baseUrl:
          process.env.EXPO_PUBLIC_API_BASE_URL ||
          (() => {
            throw new Error('EXPO_PUBLIC_API_BASE_URL is not set for baseUrl in baseQuery')
          })(),
        baseHeaders: overrideHeaders ? { ...baseHeaders, ...overrideHeaders } : baseHeaders,
      },
      // @ts-expect-error King: since we are converting endpoint args to optional for DX
      // we have a slight divergence
      args,
    ),
  )
}

function throwResponse(response: Awaited<ReturnType<typeof baseFetch>>) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/consistent-type-assertions
  response['headers'] = Object.fromEntries(response.headers.entries()) as unknown as Headers
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw response
}

export const baseQuery = (async <Route extends AppRoute>(query: BaseQueryArgs<Route>) => {
  try {
    const response = await baseFetch(query)

    if (isErrorResponse(response)) {
      throwOriginalResponse: {
        if (response.status === 401 && response.body?.message === 'Unauthorized') {
          const { tokens, setEntireResponse, clearTokens } = useTokenStore.getState()
          if (!tokens) break throwOriginalResponse
          const refreshToken = 'refreshToken' in tokens ? tokens.refreshToken : undefined
          if (!refreshToken) break throwOriginalResponse

          let newAccessToken: string
          try {
            const res = await refreshAccessToken({ refreshToken })
            const accessToken = res?.refreshToken.accessToken
            if (!accessToken) throw new Error('Failed to obtain new access token')
            setEntireResponse(res.refreshToken)
            newAccessToken = accessToken
          } catch (error) {
            clearTokens()
            console.error('Error refreshing access token:', stringifyMessage(error))
            throw error
          }

          // Retry with the new access token explicitly set in the authorization header like in apolloClient
          const responseForRetryWithRefreshedAccessToken = await baseFetch(query, {
            authorization: `Bearer ${newAccessToken}`,
          })
          if (isErrorResponse(responseForRetryWithRefreshedAccessToken)) {
            throwResponse(responseForRetryWithRefreshedAccessToken)
          }

          return {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            data: responseForRetryWithRefreshedAccessToken.body as SuccessResult<Route>['body'],
            meta: responseForRetryWithRefreshedAccessToken,
          }
        }
      }

      throwResponse(response)
    }

    return {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      data: response.body as SuccessResult<Route>['body'],
      meta: response,
    } as const
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-empty-object-type
    return { error, meta: error as {} } as const
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments, @typescript-eslint/no-empty-object-type
}) satisfies BaseQueryFn<BaseQueryArgs<AppRoute>, unknown, unknown, unknown, {}>

export type BaseQuery = typeof baseQuery
